<script setup lang="ts">
import { useLocale } from '@/plugins/translation';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import {
  CoverFeatureItem,
  MaterialCover,
  MaterialPreview,
  CoverFeatures,
  ContentViewTable,
} from '@ilteducation/components';
import { ContentWithDuplicates, useMaterialsStore } from '@ilteducation/content';
import { computed, onMounted, ref, watch } from 'vue';
import MaterialDialog from '@/components/material/MaterialDialog.vue';
import { useDisplay } from 'vuetify';
import { VDataTableVirtual } from 'vuetify/components';
import { useDialogFocus } from '@ilteducation/components';
import { useRoute, useRouter } from 'vue-router';
import { useDocumentInfiniteScroll } from '@/composables/use-document-infinite-scroll';
import { useClientAnalytics } from '@ilteducation/client-analytics';

const emits = defineEmits<{
  (e: 'load-more'): void;
}>();

const { t, l } = useLocale();
const { xs } = useDisplay();

const props = defineProps<{
  deduplicatedMaterials: MaterialV1[];
  isLoading: boolean;
  hasMore: boolean;
  // Where the user reached this material list from, so we can track how materials are discovered.
  source: 'materials_search' | 'video_related_materials';
}>();

const { sendAnalyticsEvent } = useClientAnalytics();

const trackMaterialView = (material: MaterialV1) => {
  sendAnalyticsEvent('AppUserEventType', {
    eventName: 'view_material',
    metadata: {
      materialId: material.id,
      title: material.title,
      language: material.language,
      source: props.source,
    },
  });
};
const selectedMaterial = ref<ContentWithDuplicates>();
const previewDialog = ref(false);
const { rememberFocus } = useDialogFocus([previewDialog]);
const contentTable = ref();
const router = useRouter();
const route = useRoute();
const { getById } = useMaterialsStore();

watch([selectedMaterial, previewDialog], ([sm, pd]) => {
  if (sm && pd) {
    router.replace({
      query: {
        ...route.query,
        materialId: sm ? sm.id : undefined,
      },
    });
  } else {
    const params = new URLSearchParams(route.query as Record<string, string>);
    params.delete('materialId');

    // Update the URL without triggering navigation, to avoid content jumping when the dialog closes
    window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
  }
});

onMounted(async () => {
  const materialId = route.query.materialId as string | undefined;
  if (materialId) {
    const material = await getById(materialId);
    if (material) {
      selectedMaterial.value = material;
      previewDialog.value = true;
      trackMaterialView(material as MaterialV1);
    }
  }
});

// Infinite scroll detection
useDocumentInfiniteScroll({
  isLoading: computed(() => props.isLoading),
  hasMore: computed(() => props.hasMore),
  itemCount: computed(() => props.deduplicatedMaterials.length),
  onLoadMore: () => emits('load-more'),
});

type ReadonlyHeaders = VDataTableVirtual['headers'];
const headers = computed(
  () =>
    [
      { key: 'id', value: 'id', sortable: false, align: 'start', width: '55px' },
      { title: '', key: 'title', value: 'title', sortable: false },
    ] as ReadonlyHeaders,
);

const getFeatures = (material: ContentWithDuplicates): CoverFeatureItem[] => {
  const features: CoverFeatureItem[] = [];
  if (material.duplicatesCount && material.duplicatesCount > 1) {
    features.push({
      id: 'languages',
      type: 'languages',
      color: 'rgb(var(--v-theme-secondary-container))',
      tooltip: t(l.material.available_languages_tooltip),
    });
  }
  return features;
};

const onMaterialClick = (material: ContentWithDuplicates) => {
  trackMaterialView(material as MaterialV1);
  rememberFocus();
  selectedMaterial.value = material;
  previewDialog.value = true;
};

const onOpenContent = (id: string) => {
  const found = props.deduplicatedMaterials.find((material) => material.id === id);
  if (found) {
    onMaterialClick(found);
  }
};

const onChangeMaterial = (material: MaterialV1) => {
  selectedMaterial.value = material;
};
</script>

<template>
  <div v-if="!xs">
    <v-row v-if="deduplicatedMaterials.length !== 0" class="gc-sm-6 gc-md-12 gr-4 my-6 pl-6">
      <v-col v-for="material in deduplicatedMaterials" cols="auto" :key="material.id" class="pa-2">
        <MaterialPreview
          class="ma-2"
          :material="material"
          :duplicates-count="(material as ContentWithDuplicates).duplicatesCount"
          :hover-overflow-hidden="false"
          hover-rounded-class="rounded-sm"
          @on-material-click="onMaterialClick"
        >
          <template #coverFeatures>
            <CoverFeatures
              :features="getFeatures(material)"
              direction="vertical"
              position="left"
              :icon-size="20"
              size="large"
              :border="true"
              :extra-item-classes="['rounded-circle', 'feature-border']"
            >
            </CoverFeatures>
          </template>
        </MaterialPreview>
      </v-col>
    </v-row>
    <div v-if="deduplicatedMaterials.length === 0 && !isLoading" class="text-center my-6">
      {{ t(l.label_no_matches) }}
    </div>
    <div v-if="isLoading" class="d-flex justify-center my-6">
      <v-progress-circular indeterminate size="64" color="primary" />
    </div>
  </div>
  <div v-if="xs" ref="contentTable" class="content-wrap">
    <ContentViewTable
      :parent-scroll-element="contentTable"
      :model-value="[]"
      :rounded="false"
      :content-items="deduplicatedMaterials"
      :heads="headers"
      :is-loading="isLoading"
      :has-more-content="hasMore"
      @open-content="onOpenContent"
      @on-load-content="() => emits('load-more')"
    >
      <template #itemCover="{ item }">
        <MaterialCover :id="item.id" :duplicates-count="(item as ContentWithDuplicates).duplicatesCount" cover />
      </template>
    </ContentViewTable>
    <template v-if="deduplicatedMaterials.length === 0 && !isLoading">
      {{ t(l.label_no_matches) }}
    </template>
  </div>
  <MaterialDialog
    v-if="selectedMaterial"
    :model-value="previewDialog"
    :selected-material="selectedMaterial as MaterialV1"
    @update:model-value="(val) => (previewDialog = val)"
    @update:selected-material="onChangeMaterial"
  />
</template>

<style scoped lang="scss"></style>
